import {
  AccountBalanceWalletOutlined,
  AccountTreeOutlined,
  AdminPanelSettingsOutlined,
  AltRouteOutlined,
  DescriptionOutlined,
  EventAvailableOutlined,
  HubOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import type { SectionNavigationGroup, SectionNavigationItem } from '../design-system';
import { heydayPageIcons } from './heydayPageIcons';
import {
  canLoadHeydayPage,
  heydayPageLabel,
  type HeydayPage,
} from './heydayPageManifest';

export type { HeydayPage } from './heydayPageManifest';
export { allHeydayPages, heydayPageLabel, isEmployeeHeydayPage } from './heydayPageManifest';

type CapabilityContext = {
  scopes: readonly string[];
  roles: readonly string[];
  fixtureMode?: boolean;
  availableFeatures?: readonly string[];
  managementApps?: readonly string[];
};

export type HeydayNavigationModel = {
  employeeGroups: readonly SectionNavigationGroup<HeydayPage>[];
  adminGroups: readonly SectionNavigationGroup<HeydayPage>[];
  visiblePages: ReadonlySet<HeydayPage>;
  canAccessAdmin: boolean;
  adminDefault: HeydayPage;
};

function navigationItem(id: HeydayPage): SectionNavigationItem<HeydayPage> {
  return { id, label: heydayPageLabel(id), icon: heydayPageIcons[id] };
}

const myGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'my-overview', items: [
    navigationItem('my-home'),
    navigationItem('my-tasks'),
    navigationItem('directory'),
  ] },
  { id: 'my-records', label: '인사', items: [
    navigationItem('my-absence'),
    navigationItem('my-documents'),
  ] },
  { id: 'my-work', label: '업무', items: [
    navigationItem('my-workflows'),
    navigationItem('my-workflow-requests'),
  ] },
];

const adminDashboardGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'admin-overview', items: [
    navigationItem('home'),
  ] },
];

const adminOperationsGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'admin-workforce', label: '구성원 및 조직', icon: <AccountTreeOutlined />, collapsible: true, items: [
    navigationItem('people'),
    navigationItem('organization'),
    navigationItem('staffing'),
    navigationItem('lifecycle'),
  ] },
  { id: 'admin-absence', label: '휴가 관리', icon: <EventAvailableOutlined />, collapsible: true, items: [
    navigationItem('absence'),
    navigationItem('absence-settings'),
  ] },
  { id: 'admin-rewards', label: '보상 및 정원', icon: <AccountBalanceWalletOutlined />, collapsible: true, items: [
    navigationItem('rewards'),
    navigationItem('headcount'),
  ] },
];

const adminDocumentGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'admin-documents', label: '문서 관리', icon: <DescriptionOutlined />, collapsible: true, items: [
    navigationItem('documents'),
    navigationItem('certificates'),
    navigationItem('admin-certificates'),
  ] },
];

const adminWorkflowGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'admin-workflow-settings', label: '워크플로우 설정', icon: <AltRouteOutlined />, collapsible: true, items: [
    navigationItem('workflow-requests'),
    navigationItem('workflow'),
    navigationItem('workflow-templates'),
    navigationItem('workflow-approval-settings'),
  ] },
];

const adminRecruitingGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'admin-recruiting', label: '채용 관리', icon: <HubOutlined />, collapsible: true, items: [
    navigationItem('recruiting-home'),
    navigationItem('requisitions'),
    navigationItem('candidates'),
    navigationItem('pipeline'),
    navigationItem('interviews'),
    navigationItem('offers'),
    navigationItem('recruiting-reports'),
  ] },
];

const adminBasicSettingsGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'admin-basic-settings', label: '기본 정보', icon: <SettingsOutlined />, collapsible: true, items: [
    navigationItem('admin-company-info'),
    navigationItem('admin-affiliates'),
    navigationItem('admin-reference-data'),
    navigationItem('admin-company-seals'),
    navigationItem('admin-getting-started'),
  ] },
];

const adminCertificateSettingsGroups: readonly SectionNavigationGroup<HeydayPage>[] = adminDocumentGroups
  .map((group) => ({
    ...group,
    items: group.items.filter((item) => item.id === 'admin-certificates'),
  }));

const adminSystemGroups: readonly SectionNavigationGroup<HeydayPage>[] = [
  { id: 'admin-system', label: '시스템 관리', icon: <AdminPanelSettingsOutlined />, collapsible: true, items: [
    navigationItem('admin-security'),
    navigationItem('admin-features'),
    navigationItem('admin-integrations'),
    navigationItem('admin-audit'),
    navigationItem('admin-appearance'),
  ] },
];

const legacyAdminOperationsScopes = new Set([
  'person.read', 'person.write',
  'staffing.write',
  'business_process.read', 'business_process.approve', 'business_process.cancel',
  'absence.manage',
  'rewards.read', 'rewards.write', 'rewards.approve', 'rewards.sensitive.read',
  'headcount.read', 'headcount.write',
  'documents.write',
  'certificates.operations.read', 'certificates.issue', 'certificates.revoke',
  'lifecycle.read', 'lifecycle.write',
  'workflow.read', 'workflow.write', 'workflow.run',
  'workflow.template.write', 'workflow.template.publish',
  'workflow.request.read.all', 'workflow.request.export',
  'bulk.read', 'bulk.write',
  'reports.read', 'reports.export',
]);

// Approval processing, bulk jobs and reporting remain common operations. App
// management itself comes from server-evaluated non-self capabilities, so a
// personal rewards grant cannot expose the administrator workspace.
const commonOperationsScopes = new Set([
  'business_process.read', 'business_process.approve', 'business_process.cancel',
  'workflow.read', 'workflow.write', 'workflow.run',
  'bulk.read', 'bulk.write', 'reports.read', 'reports.export',
]);

function filterGroups(groups: readonly SectionNavigationGroup<HeydayPage>[], scopes: ReadonlySet<string>, fixtureMode: boolean, availableFeatures?: readonly string[], managementApps?: readonly string[]) {
  return groups.flatMap((group) => {
    const context = { scopes: [...scopes], fixtureMode, availableFeatures, managementApps };
    const items = group.items.filter((item) => canLoadHeydayPage(item.id, context) && (item.id !== 'workflow-approval-settings' || !canLoadHeydayPage('workflow-templates', context)));
    return items.length ? [{ ...group, items }] : [];
  });
}

export function createHeydayNavigation({ scopes: scopeList, roles, fixtureMode = false, availableFeatures, managementApps }: CapabilityContext): HeydayNavigationModel {
  const scopes = new Set(scopeList);
  const normalizedRoles = roles.map((role) => role.toLowerCase());
  const hrRole = normalizedRoles.some((role) => /^(heyday_admin|hr|hr partner|hr administrator|people & culture|people operations)$/.test(role));
  const operations = fixtureMode || (managementApps === undefined
    ? hrRole || scopeList.some((scope) => legacyAdminOperationsScopes.has(scope))
    : managementApps.some((app) => app !== 'recruiting') || scopeList.some((scope) => commonOperationsScopes.has(scope)));
  const recruiting = fixtureMode || scopeList.some((scope) => scope.startsWith('recruiting.')) || normalizedRoles.some((role) => /recruit|talent acquisition/.test(role));
  const systemAdmin = fixtureMode || normalizedRoles.includes('heyday_admin') || scopeList.some((scope) => scope !== 'security.impersonate' && /^(security|integration|audit)\./.test(scope)) || normalizedRoles.some((role) => /system admin|security admin/.test(role));
  const employeeGroups = filterGroups(myGroups, scopes, fixtureMode, availableFeatures, managementApps);
  const systemGroups = filterGroups(adminSystemGroups, scopes, fixtureMode, availableFeatures, managementApps).flatMap((group) => {
    const items = group.items.filter((item) => item.id === 'admin-appearance' || systemAdmin);
    return items.length ? [{ ...group, items }] : [];
  });

  const adminFeatureGroups = [
    ...(operations ? filterGroups(adminOperationsGroups, scopes, fixtureMode, availableFeatures, managementApps) : []),
    ...(recruiting ? filterGroups(adminRecruitingGroups, scopes, fixtureMode, availableFeatures, managementApps) : []),
    ...filterGroups(operations ? adminDocumentGroups : adminCertificateSettingsGroups, scopes, fixtureMode, availableFeatures, managementApps),
    ...(operations ? filterGroups(adminWorkflowGroups, scopes, fixtureMode, availableFeatures, managementApps) : []),
    ...filterGroups(adminBasicSettingsGroups, scopes, fixtureMode, availableFeatures, managementApps),
    ...systemGroups,
  ];
  const adminGroups = [
    ...(adminFeatureGroups.length > 0 ? filterGroups(adminDashboardGroups, scopes, fixtureMode, availableFeatures, managementApps) : []),
    ...adminFeatureGroups,
  ];
  const visiblePages = new Set<HeydayPage>(['my-profile']);
  for (const group of employeeGroups) for (const item of group.items) visiblePages.add(item.id);
  for (const group of adminGroups) for (const item of group.items) visiblePages.add(item.id);
  if (operations && canLoadHeydayPage('workflow-approval-settings', { scopes: scopeList, fixtureMode, availableFeatures, managementApps })) visiblePages.add('workflow-approval-settings');
  if (operations && canLoadHeydayPage('inbox', { scopes: scopeList, fixtureMode, availableFeatures, managementApps })) visiblePages.add('inbox');
  if (operations && canLoadHeydayPage('bulk', { scopes: scopeList, fixtureMode, availableFeatures, managementApps })) visiblePages.add('bulk');
  if (scopes.has('tenant.settings.read') && canLoadHeydayPage('organization', { scopes: scopeList, fixtureMode, availableFeatures, managementApps })) visiblePages.add('organization');
  const firstAdminPage = adminGroups.flatMap((group) => group.items)[0]?.id ?? 'my-home';

  return {
    employeeGroups,
    adminGroups,
    visiblePages,
    canAccessAdmin: adminGroups.length > 0,
    adminDefault: firstAdminPage,
  };
}
